import type { FontFamilyPref, LetterSpacing, LineHeight, ThemeMode } from '@/lib/store/themeStore';
import { isFontFamilyPref } from '@/lib/store/themeStore';
import { resolveAccommodations } from '@/pages/experiences/utils/realtimeExperienceHelper';
import { selectCurrentUser, useAuthStore } from '@/store/authStore';
import { selectExperienceSettings, useExperienceStore } from '@/store/experienceStore';
import { filter, sortBy } from 'lodash';
import { create, useStore as useZustandStore } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { shallow } from 'zustand/shallow';

export const useShallowStore = (selector: any) => useZustandStore(selector, shallow as any);

// Health of the offline (IndexedDB / Dexie) database, reported by openOfflineDB()
// in OfflineDatabase.ts. Default 'open' matches the v2 selector default and avoids
// a first-paint banner flash before the open attempt resolves.
//   'open'          → db.open() resolved; saves should work
//   'closed'        → db.open() rejected at runtime (quota, corruption, eviction, etc.)
//   'not_supported' → window.indexedDB is unavailable (e.g., Safari private mode)
export type OfflineDBStatus = 'open' | 'closed' | 'not_supported';

// Notepad window geometry. The pairs are independently nullable: a drag must
// not freeze the responsive default width, a resize must not freeze the
// anchored default position.
export interface NotepadGeometry {
  x: number | null;
  y: number | null;
  w: number | null;
  h: number | null;
}

interface ExperienceRealtimeState {
  experience_info: any;
  experience_info_loading: boolean;
  experience_info_error: string | null;
  user_info: any;
  // True once the first realtime user_info snapshot landed (any provider).
  user_info_hydrated: boolean;
  // Failed user_info writes park here; the next write merges them under its own payload.
  pending_user_info: any;
  // Session-only UI state — outside user_info so it never reaches the wire.
  notepad_geometry: NotepadGeometry;
  user_segment_interactions: any;
  saving_status: any;
  user_info_loading: boolean;
  user_info_error: string | null;
  user_segment_interaction_loading: boolean;
  user_segment_interaction_error: string | null;
  logs: any;
  logs_loading: boolean;
  logs_error: string | null;
  user_responses: any;
  user_responses_loading: boolean;
  user_responses_error: string | null;
  loading: boolean;
  error: string | null;
  activeChats: any;
  isMobileUploadSession: boolean;
  // Focus lost state properties
  isFocusLost: boolean;
  focusLostExplanation: string;
  focusLostModalVisible: boolean;
  showStudentCodeAckModal: boolean;
  skipFocusLostModal: boolean;
  joinTestBtnLoadingForExperience: any;
  endSessionBtnLoading: boolean;
  serverTimeOffset: number;
  quitRequested: boolean;
  // Text highlighter state properties
  highlightTextEnabled: boolean;
  selectedHighlightColor: string;
  commentsVisible: boolean;
  // Reading line tracker state property
  readingLineEnabled: boolean;
  skipRealtimeLogs: boolean;
  offlineDBStatus: OfflineDBStatus;
}

// Contract for setLocalUserInfo — a typo here silently degraded to
// local-wins on an exam-critical path when the options bag was `any`.
export interface SetLocalUserInfoOptions {
  /** false = wholesale user_info replacement (teardown reset). Default true. */
  merge?: boolean;
  /** true = deliberate progress reset: incoming questions_status replaces the union. */
  replaceQuestionsStatus?: boolean;
}

interface ExperienceRealtimeActions {
  setLocalExperienceInfo: (experienceInfo: any, options?: any) => void;
  setLocalUserInfo: (userInfo: any, options?: SetLocalUserInfoOptions) => void;
  /** merge:false = wholesale replacement — `setPendingUserInfo({}, { merge: false })` clears. */
  setPendingUserInfo: (payload: any, options?: { merge?: boolean }) => void;
  /** Merges the given pair(s); the pair left out keeps its value. */
  setNotepadGeometry: (geometry: Partial<NotepadGeometry>) => void;
  setLocalSegmentInteraction: (
    segmentInteractionId: string,
    segmentInteraction: any,
    options?: any
  ) => void;
  setLocalSegmentInteractions: (segmentInteractions: any, options?: any) => void;
  setLocalLogs: (logs: any) => void;
  setLocalUserResponse: (responseIdentifier: string, response: any, options?: any) => void;
  setLocalUserResponses: (responses: any, options?: any) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
  setFirstActiveChat: (firstActiveChat: any) => void;
  setIsMobileUploadSession: (isUploadDeviceSession: boolean) => void;
  // Focus lost actions
  setIsFocusLost: (value: boolean) => void;
  setFocusLostExplanation: (value: string) => void;
  setFocusLostModalVisible: (value: boolean) => void;
  setShowStudentCodeAckModal: (value: boolean) => void;
  resetFocusLostState: () => void;
  setSkipFocusLostModal: (value: boolean) => void;
  setSavingStatus: (savingStatus: any) => void;
  setJoinTestBtnLoadingForExperience: (uid: string | null) => void;
  setEndSessionBtnLoading: (loading: boolean) => void;
  setServerTimeOffset: (offset: number) => void;
  setQuitRequested: (value: boolean) => void;
  // Text highlighter actions
  setHighlightTextEnabled: (value: boolean) => void;
  setSelectedHighlightColor: (color: string) => void;
  setCommentsVisible: (value: boolean) => void;
  // Reading line tracker action
  setReadingLineEnabled: (value: boolean) => void;
  setSkipRealtimeLogs: (value: boolean) => void;
  setOfflineDBStatus: (status: OfflineDBStatus) => void;
}

type ExperienceRealtimeStore = ExperienceRealtimeState & ExperienceRealtimeActions;

const initialState: ExperienceRealtimeState = {
  experience_info: {},
  experience_info_loading: false,
  experience_info_error: null,
  user_info: {},
  user_info_hydrated: false,
  pending_user_info: {},
  notepad_geometry: { x: null, y: null, w: null, h: null },
  user_segment_interactions: {},
  user_segment_interaction_loading: false,
  user_segment_interaction_error: null,
  saving_status: {},
  user_info_loading: false,
  user_info_error: null,
  logs: [],
  logs_loading: false,
  logs_error: null,
  user_responses: {},
  user_responses_loading: false,
  user_responses_error: null,
  loading: false,
  error: null,
  activeChats: null,
  isMobileUploadSession: false,
  // Focus lost state properties
  isFocusLost: false,
  focusLostExplanation: '',
  focusLostModalVisible: false,
  showStudentCodeAckModal: false,
  skipFocusLostModal: false,
  joinTestBtnLoadingForExperience: null,
  endSessionBtnLoading: false,
  serverTimeOffset: 0,
  quitRequested: false,
  // Text highlighter state properties
  highlightTextEnabled: false,
  selectedHighlightColor: '#7dffff',
  commentsVisible: true,
  // Reading line tracker state property
  readingLineEnabled: false,
  skipRealtimeLogs: false,
  offlineDBStatus: 'open',
};

// J25 Fix 10 — keyed merge for user_info.questions_status (exam-critical progress).
// Union by `segment_id|topic_id`: 'local' writes win per key (last-write-wins);
// 'server' echoes may only ADD unknown keys — they never delete an existing key or
// downgrade a locally-newer entry (a reconnect echo can carry an older array).
// Existing order is preserved and new keys append, so rendering never shuffles.
// Identity-preserving: forks lazily — untouched entries keep their refs and a
// no-op merge returns `existing` unchanged, so useShallow subscribers (rail
// chips, attempt panel) skip re-rendering on no-op server echoes.
export const mergeQuestionsStatus = (existing: any, incoming: any): any[] => {
  const base: any[] = Array.isArray(existing) ? existing : [];
  const indexByKey = new Map<string, number>();
  base.forEach((q, idx) => indexByKey.set(`${q?.segment_id}|${q?.topic_id}`, idx));
  let merged: any[] | null = null;
  (Array.isArray(incoming) ? incoming : []).forEach((q: any) => {
    const key = `${q?.segment_id}|${q?.topic_id}`;
    const existingIdx = indexByKey.get(key);
    if (existingIdx === undefined) {
      merged = merged ?? [...base];
      indexByKey.set(key, merged.length);
      merged.push({ ...q });
    } else {
      const current = (merged ?? base)[existingIdx];
      const changed = Object.keys(q ?? {}).some((k) => q[k] !== current?.[k]);
      if (changed) {
        merged = merged ?? [...base];
        merged[existingIdx] = { ...current, ...q };
      }
    }
  });
  return merged ?? base;
};

export const useRealtimeExperienceStore = create<ExperienceRealtimeStore>()(
  immer((set) => ({
    ...initialState,
    setLocalExperienceInfo: (experienceInfo, options = { merge: true }) =>
      set((state) => {
        if (options?.merge) {
          state.experience_info = { ...state.experience_info, ...experienceInfo };
        } else {
          state.experience_info = experienceInfo;
        }
      }),

    setLocalUserInfo: (userInfo, options = { merge: true }) =>
      set((state) => {
        // Default stays merge:true even for partial options objects.
        if (options?.merge ?? true) {
          const current = state.user_info ?? {};
          const next: any = { ...current, ...userInfo };
          // J25 Fix 10: questions_status merges by question key — a whole-array
          // replace let a stale/older array delete other questions' progress.
          // `replaceQuestionsStatus: true` is the explicit opt-out for deliberate resets.
          if (Array.isArray(userInfo?.questions_status) && !options?.replaceQuestionsStatus) {
            next.questions_status = mergeQuestionsStatus(
              current.questions_status,
              userInfo.questions_status
            );
            // attempt_count derives from the merged union — the incoming payload's
            // own count may predate entries the merge preserved.
            next.attempt_count = next.questions_status.filter(
              (q: any) => q?.attempted === true
            ).length;
          }
          // Merge per leaf: a write carries only the changed fields, so a replace would amputate untouched siblings.
          if (userInfo?.accessibility && current.accessibility) {
            next.accessibility = { ...current.accessibility, ...userInfo.accessibility };
          }
          state.user_info = next;
        } else {
          state.user_info = userInfo;
        }
        state.user_info_hydrated = true;
      }),

    setPendingUserInfo: (payload, options = { merge: true }) =>
      set((state) => {
        if (options?.merge ?? true) {
          state.pending_user_info = { ...state.pending_user_info, ...payload };
        } else {
          state.pending_user_info = payload;
        }
      }),

    setNotepadGeometry: (geometry) =>
      set((state) => {
        state.notepad_geometry = { ...state.notepad_geometry, ...geometry };
      }),

    setLocalSegmentInteraction: (
      segmentInteractionId: string,
      segmentInteraction: any,
      options = { merge: true }
    ) =>
      set((state) => {
        if (options?.merge) {
          const existingSegmentInteraction =
            state.user_segment_interactions[segmentInteractionId] ?? {};
          state.user_segment_interactions = {
            ...state.user_segment_interactions,
            [segmentInteractionId]: {
              ...existingSegmentInteraction,
              ...segmentInteraction,
            },
          };
        } else {
          state.user_segment_interactions = {
            ...state.user_segment_interactions,
            [segmentInteractionId]: segmentInteraction,
          };
        }
      }),

    setLocalSegmentInteractions: (userSegmentInteractions, options = { merge: true }) =>
      set((state) => {
        state.user_segment_interactions = userSegmentInteractions;
      }),

    setLocalLogs: (logs) =>
      set((state) => {
        state.logs = logs;
      }),

    setLocalUserResponse: (responseIdentifier, response, options = { merge: true }) =>
      set((state) => {
        if (options?.merge) {
          const existingResponse = state.user_responses[responseIdentifier] ?? {};
          state.user_responses = {
            ...state.user_responses,
            [responseIdentifier]: {
              ...existingResponse,
              ...response,
            },
          };
        } else {
          state.user_responses = {
            ...state.user_responses,
            [responseIdentifier]: response,
          };
        }
      }),

    setLocalUserResponses: (responses, options = { merge: true }) =>
      set((state) => {
        state.user_responses = responses;
      }),

    // Utility setters
    setLoading: (loading) =>
      set((state) => {
        state.loading = loading;
      }),

    setError: (error) =>
      set((state) => {
        state.error = error;
      }),

    setFirstActiveChat: (firstActiveChat) =>
      set((state) => {
        state.activeChats = firstActiveChat;
      }),

    setIsMobileUploadSession: (isMobileUploadSession) =>
      set((state) => {
        state.isMobileUploadSession = isMobileUploadSession;
      }),

    // Focus lost actions
    setIsFocusLost: (value) =>
      set((state) => {
        state.isFocusLost = value;
      }),
    setFocusLostExplanation: (value) =>
      set((state) => {
        state.focusLostExplanation = value;
      }),
    setFocusLostModalVisible: (value) =>
      set((state) => {
        state.focusLostModalVisible = value;
      }),
    setShowStudentCodeAckModal: (value) =>
      set((state) => {
        state.showStudentCodeAckModal = value;
      }),
    setSkipFocusLostModal: (value) =>
      set((state) => {
        state.skipFocusLostModal = value;
      }),
    resetFocusLostState: () =>
      set((state) => {
        return {
          ...state,
          isFocusLost: false,
          focusLostExplanation: '',
          focusLostModalVisible: false,
          showStudentCodeAckModal: false,
          skipFocusLostModal: false,
        };
      }),

    setSavingStatus: ({ segment_id, status: updatedStatus }) =>
      set((state) => {
        const savingStatus = state.saving_status;
        const newStatus = {
          ...savingStatus[segment_id],
          ...updatedStatus,
        };

        return {
          // ...state,
          saving_status: {
            ...savingStatus,
            latest: newStatus,
            [segment_id]: newStatus,
          },
        };
      }),

    setJoinTestBtnLoadingForExperience: (uid) =>
      set((state) => {
        state.joinTestBtnLoadingForExperience = uid;
      }),

    setEndSessionBtnLoading: (loading) =>
      set((state) => {
        state.endSessionBtnLoading = loading;
      }),

    setServerTimeOffset: (offset) =>
      set((state) => {
        state.serverTimeOffset = offset;
      }),

    setQuitRequested: (value) =>
      set((state) => {
        state.quitRequested = value;
      }),

    // Text highlighter actions
    setHighlightTextEnabled: (value) =>
      set((state) => {
        state.highlightTextEnabled = value;
      }),
    setSelectedHighlightColor: (color) =>
      set((state) => {
        state.selectedHighlightColor = color;
      }),
    setCommentsVisible: (value: boolean) =>
      set((state) => {
        state.commentsVisible = value;
      }),

    // Reading line tracker action
    setReadingLineEnabled: (value: boolean) =>
      set((state) => {
        state.readingLineEnabled = value;
      }),
    setSkipRealtimeLogs: (value: boolean) =>
      set((state) => {
        state.skipRealtimeLogs = value;
      }),

    setOfflineDBStatus: (status: OfflineDBStatus) =>
      set((state) => {
        state.offlineDBStatus = status;
      }),

    // Reset function - clear all data and states
    reset: () =>
      set((state) => {
        return { ...state, ...initialState };
      }),
  }))
);

export const firstActiveChat = (state: ExperienceRealtimeStore) => state.activeChats ?? null;

export const selectIsMobileUploadSession = (state: ExperienceRealtimeStore) =>
  state.isMobileUploadSession;

////////////////////////////////////////////////////////////
// Experience Info Selectors
////////////////////////////////////////////////////////////
export const selectExperienceInfo = (state: ExperienceRealtimeStore) => state.experience_info;

export const selectExperienceInfoLoading = (state: ExperienceRealtimeStore) =>
  state.experience_info_loading;

////////////////////////////////////////////////////////////
// User Info Selectors
////////////////////////////////////////////////////////////

export const selectTalviewDisabled = (state: ExperienceRealtimeStore) => {
  const userInfo = selectUserInfo(state);
  return userInfo?.talview?.disabled ?? false;
};

export const selectUserInfo = (state: ExperienceRealtimeStore) => state.user_info;

export const selectUserInfoHydrated = (state: ExperienceRealtimeStore) => state.user_info_hydrated;

export const selectCurrentViewFromUserInfo = (state: ExperienceRealtimeStore) => {
  return state.user_info?.currentView ?? null;
};

/** Server-owned attempt number on the realtime doc (shielded by
 *  onlinePriorityKeys, so the Dexie merge can never stale it). Higher than the
 *  payload's latest taken experience_user.attempt_no = a retake is open. */
export const selectAttemptNoFromUserInfo = (state: ExperienceRealtimeStore) =>
  state.user_info?.attempt_no ?? null;

export const selectAttemptCount = (state: ExperienceRealtimeStore) =>
  state.user_info?.attempt_count ?? 0;

export const selectTotalEngagementPoints = (state: ExperienceRealtimeStore) =>
  state.user_info?.total_e_points ?? 0;

export const selectActiveSegmentInfo = (state: ExperienceRealtimeStore) =>
  state.user_info?.active_segment_info ?? null;

export const selectQuestionsStatus = (state: ExperienceRealtimeStore) =>
  state.user_info?.questions_status ?? [];

export const selectAttachmentsTotalCount = (state: ExperienceRealtimeStore) =>
  state.user_info?.attachments_count ?? 0;

export const selectIsTimeUp = (state: ExperienceRealtimeStore) =>
  state.user_info?.is_time_up ?? false;

export const selectFocusLostAt = (state: ExperienceRealtimeStore) =>
  state.user_info?.focusLostAt ?? new Date().getTime();

export const selectRequestResumeSessionId = (state: ExperienceRealtimeStore) =>
  state.user_info?.request_resume_session_id ?? null;

export const selectCurrentSessionIdFromSessionStorage = (state: ExperienceRealtimeStore) =>
  state.user_info?.currentSessionIdFromSessionStorage ?? null;

export const selectIsUploadingFinished = (state: ExperienceRealtimeStore) =>
  state.user_info?.is_uploading_finished ?? false;

export const selectMobileUploadMode = (state: ExperienceRealtimeStore) =>
  state.user_info?.mobile_upload_mode ?? false;

export const selectSessionId = (state: ExperienceRealtimeStore) =>
  state.user_info?.session_id ?? null;

export const selectUploadSessionId = (state: ExperienceRealtimeStore) => {
  return state.user_info?.uploadDeviceInfo?.upload_session_id ?? null;
};

export const selectUploadDeviceInfo = (state: ExperienceRealtimeStore) =>
  state.user_info?.uploadDeviceInfo ?? {};

export const selectDurationAfterReset = (state: ExperienceRealtimeStore) =>
  state.user_info?.duration_after_reset ?? null;

////////////////////////////////////////////////////////////
// Accommodations — single source of truth
////////////////////////////////////////////////////////////
// Effective accommodations, resolved live on every read (see resolveAccommodations).
// Prefer the per-key selectors below in components. The last-call cache keeps the
// returned reference stable (Zustand re-runs every subscribed selector per store
// update; ~9 per-key selectors delegate here).
let cachedInputs: [any, any, any] | null = null;
let cachedResolved: Record<string, any> = {};
export const selectResolvedAccommodations = (state: ExperienceRealtimeStore) => {
  const userList = selectCurrentUser(useAuthStore.getState())?.custom_fields?.accommodations;
  const settings = selectExperienceSettings(useExperienceStore.getState());
  const monitor = state.user_info?.accommodations;
  if (
    !cachedInputs ||
    cachedInputs[0] !== userList ||
    cachedInputs[1] !== settings ||
    cachedInputs[2] !== monitor
  ) {
    cachedInputs = [userList, settings, monitor];
    cachedResolved = resolveAccommodations(userList, settings, monitor);
  }
  return cachedResolved;
};

export const selectSpellcheckEnabled = (state: ExperienceRealtimeStore) =>
  selectResolvedAccommodations(state).spellcheck ?? false;

export const selectRequestResume = (state: ExperienceRealtimeStore) =>
  state.user_info?.request_resume ?? null;

export const selectSttAllowed = (state: ExperienceRealtimeStore) =>
  selectResolvedAccommodations(state).stt_allowed ?? false;

export const selectAllowedInBrowser = (state: ExperienceRealtimeStore) =>
  state.user_info?.allowed_in_browser ?? false;

export const selectBookmarkedSegments = (state: ExperienceRealtimeStore, sortArray?: string[]) => {
  const bookmarkedSegments = state.user_info?.bookmarks ?? [];

  if (sortArray && sortArray.length > 0) {
    return filter(sortBy(bookmarkedSegments, sortArray), { bookmarked: true });
  }

  return bookmarkedSegments;
};

export const selectTtsAllowed = (state: ExperienceRealtimeStore) =>
  selectResolvedAccommodations(state).tts_allowed ?? false;

export const selectTranslateAllowed = (state: ExperienceRealtimeStore) =>
  selectResolvedAccommodations(state).allow_student_to_translate ?? false;

export const selectTranslateType = (state: ExperienceRealtimeStore) =>
  selectResolvedAccommodations(state).translate_type ?? 'word';

/**
 * v3 `selectTranslateType`, byte-identical — the MONITOR-AUTHORED value only.
 * NOT for feature code: read `selectTranslateType` above, which resolves the
 * profile / delivery layers too. This one exists solely as the accommodation
 * NOTIFICATION's delta input (APL-12040). The resolved value is a poor change
 * signal: its 'word' fallback is indistinguishable from an explicit 'word', and
 * `selectResolvedAccommodations` reads the auth / experience stores
 * non-reactively, so a profile layer that lands after hydration only surfaces
 * on the next realtime update — usually the teacher's FIRST monitor write,
 * which then reads as a word → full_sentence change nobody made. The monitor
 * record moves only when a teacher writes it, which is exactly what the toast
 * is reporting.
 */
export const selectMonitorTranslateType = (state: ExperienceRealtimeStore) =>
  state.user_info?.accommodations?.translate_type ?? 'word';

export const selectDictionaryAllowed = (state: ExperienceRealtimeStore) =>
  selectResolvedAccommodations(state).allow_student_to_use_dictionary ?? false;

/** Reads auth, NOT the resolved record: a dyslexia value in the monitor-authored layer is ignored. */
export const selectDyslexiaFont = (state: ExperienceRealtimeStore) => {
  // themeStore's own predicate, not `!= null`: standing down for junk that setDisplayOverride rejects loses the granted font.
  if (isFontFamilyPref(selectAccessibilityFontFamily(state))) return undefined;
  const grant = selectCurrentUser(useAuthStore.getState())?.custom_fields?.accommodations;
  // custom_fields is schemaless server-side; useDyslexiaFont calls String.replace on it.
  if (
    grant?.dyslexia_font_enabled === true &&
    typeof grant?.dyslexia_font === 'string' &&
    grant.dyslexia_font
  ) {
    return grant.dyslexia_font;
  }
  return undefined;
};

// Primitives only: object reads re-render every host on unrelated snapshots.
export const selectAccessibilityFontScale = (state: ExperienceRealtimeStore): number | undefined =>
  // null is the Reset sentinel — normalize to "nothing chosen".
  state.user_info?.accessibility?.font_scale ?? undefined;
export const selectAccessibilityTheme = (state: ExperienceRealtimeStore): ThemeMode | undefined =>
  state.user_info?.accessibility?.theme;
export const selectAccessibilityLetterSpacing = (
  state: ExperienceRealtimeStore
): LetterSpacing | undefined => state.user_info?.accessibility?.letter_spacing;
export const selectAccessibilityLineHeight = (
  state: ExperienceRealtimeStore
): LineHeight | undefined => state.user_info?.accessibility?.line_height;
export const selectAccessibilityFontFamily = (
  state: ExperienceRealtimeStore
): FontFamilyPref | undefined => state.user_info?.accessibility?.font_family;

export const selectCalcEnabled = (state: ExperienceRealtimeStore) =>
  selectResolvedAccommodations(state).calc_enabled ?? false;

export const selectGraphingCalcEnabled = (state: ExperienceRealtimeStore) =>
  selectResolvedAccommodations(state).graphing_calc_enabled ?? false;

export const selectTimerMode = (state: ExperienceRealtimeStore) =>
  state.user_info?.timer_mode ?? 'show_time';

////////////////////////////////////////////////////////////
// User Info Actions
////////////////////////////////////////////////////////////
export const selectSetLocalUserInfo = (state: ExperienceRealtimeStore) => state.setLocalUserInfo;

////////////////////////////////////////////////////////////
// User Responses Selectors
////////////////////////////////////////////////////////////
/**
 * Selector for user responses with auto-detection of return type:
 * - If responseIdentifier is provided: returns single response object or empty object
 * - If no responseIdentifier: returns object with multiple responses as key-value pairs
 *
 * @param state - The realtime experience store state
 * @param params - Filter parameters { experienceId?, userId?, responseIdentifier? }
 * @returns Single response object or multiple responses object
 */
export const selectUserResponses = (state: ExperienceRealtimeStore, params: any) => {
  const { experienceId, userId, responseIdentifier } = params;

  const user_responses = state.user_responses ?? {};

  // Single response mode: return specific response by identifier
  if (responseIdentifier) {
    return user_responses[responseIdentifier] ?? {};
  }

  // Multiple responses mode: return filtered responses object
  const userResponses: Record<string, any> = {};
  for (const key in user_responses) {
    const response = user_responses[key];

    // Skip if experienceId filter doesn't match
    if (experienceId && response.experience_id !== experienceId) {
      continue;
    }

    // Skip if userId filter doesn't match
    if (userId && response.user_id !== parseInt(userId)) {
      continue;
    }

    userResponses[key] = response;
  }
  return userResponses;
};

////////////////////////////////////////////////////////////
// User Segment Info Selectors
////////////////////////////////////////////////////////////

const EMPTY_OBJECT = {};
/**
 * Selector for user segment info with auto-detection of return type:
 * - If segmentInteractionId is provided: returns single segment info object or empty object
 * - If no segmentInteractionId: returns object with multiple segment info objects as key-value pairs
 *
 * @param state - The realtime experience store state
 * @param params - Filter parameters { experienceId?, userId?, segmentId?, segmentInteractionId? }
 * @returns Single segment info object or multiple segment info object
 */

export const selectUserSegmentInteraction = (state: ExperienceRealtimeStore, params: any) => {
  const { experienceId, userId, segmentId, segmentInteractionId } = params;

  const user_segment_interactions = state.user_segment_interactions ?? {};

  // Single segment info mode: return specific segment info by identifier
  if (segmentInteractionId) {
    return user_segment_interactions[segmentInteractionId] ?? {};
  }

  // Multiple segment info mode: return filtered segment info object
  const segmentInteractionObj: Record<string, any> = {};
  for (const key in user_segment_interactions) {
    const interaction = user_segment_interactions[key];

    // Skip if experienceId filter doesn't match
    if (experienceId && interaction.experience_id !== experienceId) {
      continue;
    }

    // Skip if userId filter doesn't match
    if (userId && interaction.user_id !== parseInt(userId)) {
      continue;
    }

    // Skip if segmentId filter doesn't match
    if (segmentId && interaction.segment_id !== segmentId) {
      continue;
    }

    segmentInteractionObj[key] = interaction;
  }

  return segmentInteractionObj;
};

export const selectUserSegmentInteractionLoading = (state: ExperienceRealtimeStore) =>
  state.user_segment_interaction_loading;

export const selectUserSegmentInteractionError = (state: ExperienceRealtimeStore) =>
  state.user_segment_interaction_error;

////////////////////////////////////////////////////////////
// Logs Selectors
////////////////////////////////////////////////////////////
export const selectLogs = (state: ExperienceRealtimeStore) => state.logs;

export const selectLogsLoading = (state: ExperienceRealtimeStore) => state.logs_loading;

export const selectLogsError = (state: ExperienceRealtimeStore) => state.logs_error;

export const selectExperiencePublishedAt = (state: ExperienceRealtimeStore) =>
  state.experience_info?.published_at ?? null;

export const selectExperiencePaused = (state: ExperienceRealtimeStore) =>
  state.experience_info?.paused ?? null;

export const selectExperiencePausedAt = (state: ExperienceRealtimeStore) =>
  state.experience_info?.paused_at ?? null;

export const selectExperienceUnpausedAt = (state: ExperienceRealtimeStore) =>
  state.experience_info?.unpaused_at ?? null;

export const selectSavingStatus = (state: ExperienceRealtimeStore, segment_id: string) => {
  const savingStatus = state.saving_status;
  const segmentStatus = savingStatus[segment_id] ?? {};
  return segmentStatus;
};

export const selectFocusLostCount = (state: ExperienceRealtimeStore) =>
  state.user_info?.focus_lost_count ?? 0;
export const selectStartedAt = (state: ExperienceRealtimeStore) =>
  state.user_info?.started_at ?? null;

export const selectExtraTimeOffset = (state: ExperienceRealtimeStore) =>
  state.user_info?.extra_time_offset ?? null;

export const selectExtraTime = (state: ExperienceRealtimeStore) =>
  state.user_info?.extra_time ?? null;

export const selectExperienceHasStudentsStarted = (state: ExperienceRealtimeStore) =>
  state.experience_info?.haveStudentsStarted ?? false;

export const selectUnpausedOffset = (state: ExperienceRealtimeStore) =>
  state.user_info?.unpaused_offset ?? null;

////////////////////////////////////////////////////////////
// This Selectors are related to experience security interruption and focus lost
////////////////////////////////////////////////////////////
export const selectIsFocusLost = (state: ExperienceRealtimeStore) => state.isFocusLost;

export const selectFocusLostExplanation = (state: ExperienceRealtimeStore) =>
  state.focusLostExplanation;

export const selectFocusLostModalVisible = (state: ExperienceRealtimeStore) =>
  state.user_info?.is_focus_lost;

export const selectShowStudentCodeAckModal = (state: ExperienceRealtimeStore) =>
  state.showStudentCodeAckModal;

export const selectSkipFocusLostModal = (state: ExperienceRealtimeStore) =>
  state.skipFocusLostModal;

////////////////////////////////////////////////////////////
// Text highlighter selectors
////////////////////////////////////////////////////////////
export const selectHighlightTextEnabled = (state: ExperienceRealtimeStore) =>
  state.highlightTextEnabled;

export const selectSelectedHighlightColor = (state: ExperienceRealtimeStore) =>
  state.selectedHighlightColor;

export const selectCommentsVisible = (state: ExperienceRealtimeStore) => state.commentsVisible;

////////////////////////////////////////////////////////////
// Reading line tracker selectors
////////////////////////////////////////////////////////////
export const selectReadingLineEnabled = (state: ExperienceRealtimeStore) =>
  state.readingLineEnabled;

export const selectStudentPauseStatus = (state: ExperienceRealtimeStore) =>
  state.user_info?.pause_status ?? null;
export const selectSkipRealtimeLogs = (state: ExperienceRealtimeStore) => state.skipRealtimeLogs;

export const selectOfflineUrsLastSavedAt = (state: ExperienceRealtimeStore) =>
  state.user_info?.offline_urs_last_saved_at ?? null;

////////////////////////////////////////////////////////////
// Offline DB (Dexie) health selectors
////////////////////////////////////////////////////////////
export const selectOfflineDBStatus = (state: ExperienceRealtimeStore) => state.offlineDBStatus;

////////////////////////////////////////////////////////////
// Notepad window selectors
////////////////////////////////////////////////////////////
/** One selector for the whole object — callers destructure; never per-field. */
export const selectNotepadGeometry = (state: ExperienceRealtimeStore) => state.notepad_geometry;
